import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import rough from 'roughjs'
import '@fontsource/caveat/600.css'
import { gsap, SplitText, useGSAP, prefersReducedMotion } from '../lib/gsap'
import { useLang } from '../lib/useLang'
import BgDottedCircle from '../dotted-circle.jsx'

/* ============================================================================
   (03) Workflow — how a feature goes from a scribble to a 200 OK.
   A pinned, scroll-scrubbed story in five scenes inside one glass "app window":
   notes → Excalidraw flow → prompt-ready spec → agent in the repo → live request.
   The last scene autoplays a click, and its button keeps working for real.
   ========================================================================== */

const COPY = {
  label: { pt: 'Como eu entrego uma feature', en: 'How I ship a feature' },
  title: { pt: 'Do rascunho ao 200 OK', en: 'From scribble to 200 OK' },
}

const STEPS = [
  {
    title: { pt: 'Rascunho', en: 'Scribble' },
    desc: { pt: 'Escrevo a ideia crua: o problema, quem usa e o que nunca pode quebrar.', en: 'I dump the raw idea: the problem, who uses it, and what must never break.' },
    file: { pt: 'rascunho.txt', en: 'notes.txt' },
  },
  {
    title: { pt: 'Diagrama', en: 'Diagram' },
    desc: { pt: 'O texto vira um fluxo no Excalidraw: caminhos felizes, erros e decisões.', en: 'The text becomes an Excalidraw flow: happy paths, errors and decisions.' },
    file: { pt: 'fluxo.excalidraw', en: 'flow.excalidraw' },
  },
  {
    title: { pt: 'Spec', en: 'Spec' },
    desc: { pt: 'O fluxo vira uma spec pronta pra prompt: contrato, regras e critérios de aceite.', en: 'The flow becomes a prompt-ready spec: contract, rules and acceptance criteria.' },
    file: { pt: 'specs/transfers/spec.md', en: 'specs/transfers/spec.md' },
  },
  {
    title: { pt: 'Agente', en: 'Agent' },
    desc: { pt: 'Specs e contratos versionados no repo. O agente lê, planeja e implementa.', en: 'Specs and contracts live in the repo. The agent reads, plans and builds.' },
    file: { pt: 'claude — ~/wallet-api', en: 'claude — ~/wallet-api' },
  },
  {
    title: { pt: 'Entrega', en: 'Delivery' },
    desc: { pt: 'Código revisado, testes verdes e a feature respondendo de verdade.', en: 'Reviewed code, green tests and the feature answering for real.' },
    file: { pt: 'localhost:3000/transfer', en: 'localhost:3000/transfer' },
  },
]

/* ---------------------------------- scene 1 --------------------------------- */
const NOTES = {
  pt: [
    '// ideia: transferir saldo entre carteiras',
    'quem usa? cliente logado, carteira de origem',
    'tem que validar saldo + limite diário',
    'acima de R$ 1.000 → pedir 2FA',
    'registrar tudo no ledger (sem duplicar!)',
    'retorno: id da transação + status',
  ],
  en: [
    '// idea: move balance between wallets',
    'who? signed-in customer, source wallet',
    'must check balance + daily limit',
    'over R$ 1,000 → ask for 2FA',
    'log everything in the ledger (no dupes!)',
    'returns: transaction id + status',
  ],
}

/* ---------------------------------- scene 2 --------------------------------- */
// Hand-drawn shapes generated once with rough.js (the engine behind Excalidraw), fixed seeds.
const INK = '#c9d2e3'
const LABELS = {
  pt: { title: 'transferência entre carteiras', user: 'usuário', twofa: '2FA · otp', check: 'saldo ok?', yes: 'sim', no: 'não', err: '422 · sem saldo', sticky: 'idempotência!' },
  en: { title: 'wallet → wallet transfer', user: 'user', twofa: '2FA · otp', check: 'balance ok?', yes: 'yes', no: 'no', err: '422 · no funds', sticky: 'idempotency!' },
}

function buildDiagram(vertical) {
  const gen = rough.generator()
  const base = { roughness: 1.3, bowing: 1.1, stroke: INK, strokeWidth: 1.6, hachureGap: 7, fillWeight: 1.3 }
  const P = (d) => gen.toPaths(d)
  const arrow = (pts, seed, opts = {}) => {
    const [x1, y1] = pts[pts.length - 2]
    const [x2, y2] = pts[pts.length - 1]
    const a = Math.atan2(y2 - y1, x2 - x1)
    const L = 13
    const s = 0.5
    const head = [
      [x2 - L * Math.cos(a - s), y2 - L * Math.sin(a - s)],
      [x2, y2],
      [x2 - L * Math.cos(a + s), y2 - L * Math.sin(a + s)],
    ]
    const shaft = pts.length > 2 ? gen.curve(pts, { ...base, seed, ...opts }) : gen.line(...pts[0], ...pts[1], { ...base, seed, ...opts })
    return [...P(shaft), ...P(gen.linearPath(head, { ...base, seed: seed + 1, ...opts, strokeLineDash: undefined }))]
  }
  const blue = { fill: 'rgba(45,92,255,0.75)', fillStyle: 'hachure' }
  const green = { fill: 'rgba(64,192,87,0.75)', fillStyle: 'hachure' }
  const red = { fill: 'rgba(250,82,82,0.6)', fillStyle: 'hachure' }
  const sticky = { ...base, stroke: '#ffd43b', fill: 'rgba(255,212,59,0.16)', fillStyle: 'solid' }
  const lavender = { fill: 'rgba(125,155,255,0.6)', fillStyle: 'hachure' }
  const diamond = { fill: 'rgba(125,155,255,0.45)', fillStyle: 'cross-hatch' }
  // phones: the same flow, top to bottom (viewBox 400 × 540)
  if (vertical)
    return [
      { paths: P(gen.line(22, 56, 300, 59, { ...base, stroke: '#2D5CFF', strokeWidth: 2.6, seed: 1 })), label: { k: 'title', x: 24, y: 36, size: 28, anchor: 'start', color: '#ffffff' } },
      { paths: P(gen.ellipse(110, 122, 150, 64, { ...base, ...lavender, seed: 2 })), label: { k: 'user', x: 110, y: 122 } },
      { paths: arrow([[110, 156], [110, 196]], 3) },
      { paths: P(gen.rectangle(26, 200, 188, 60, { ...base, ...blue, seed: 5 })), label: { text: 'POST /transfers', x: 120, y: 230 } },
      { paths: arrow([[216, 230], [262, 230]], 7, { strokeLineDash: [6, 6] }) },
      { paths: P(gen.rectangle(266, 207, 112, 46, { ...base, strokeLineDash: [7, 5], seed: 9 })), label: { k: 'twofa', x: 322, y: 230, size: 21 } },
      { paths: arrow([[120, 262], [120, 304]], 11) },
      { paths: P(gen.polygon([[120, 308], [208, 370], [120, 432], [32, 370]], { ...base, ...diamond, seed: 13 })), label: { k: 'check', x: 120, y: 370, size: 21 } },
      { paths: arrow([[210, 370], [262, 370]], 15), label: { k: 'yes', x: 236, y: 353, size: 19, color: '#7D9BFF' } },
      { paths: P(gen.rectangle(266, 340, 120, 60, { ...base, ...blue, seed: 17 })), label: { text: 'ledger', x: 326, y: 370 } },
      { paths: arrow([[326, 402], [326, 450]], 19) },
      { paths: P(gen.rectangle(266, 454, 120, 52, { ...base, ...green, seed: 21 })), label: { text: '200 OK', x: 326, y: 480 } },
      { paths: arrow([[120, 434], [120, 470]], 23), label: { k: 'no', x: 140, y: 452, size: 19, color: '#fca5a5' } },
      { paths: P(gen.rectangle(46, 474, 148, 50, { ...base, ...red, seed: 25 })), label: { k: 'err', x: 120, y: 499, size: 21 } },
      { paths: P(gen.rectangle(222, 82, 160, 50, { ...sticky, seed: 27 })), label: { k: 'sticky', x: 302, y: 107, size: 24, color: '#ffd43b', rotate: -3 } },
      { paths: arrow([[250, 134], [236, 168], [204, 196]], 29, { stroke: '#ffd43b', strokeWidth: 1.4 }) },
    ]
  return [
    { paths: P(gen.line(34, 66, 330, 69, { ...base, stroke: '#2D5CFF', strokeWidth: 2.6, seed: 1 })), label: { k: 'title', x: 34, y: 46, size: 30, anchor: 'start', color: '#ffffff' } },
    { paths: P(gen.ellipse(95, 175, 132, 72, { ...base, fill: 'rgba(125,155,255,0.6)', fillStyle: 'hachure', seed: 2 })), label: { k: 'user', x: 95, y: 175 } },
    { paths: arrow([[163, 175], [222, 175]], 3) },
    { paths: P(gen.rectangle(228, 143, 172, 64, { ...base, ...blue, seed: 5 })), label: { text: 'POST /transfers', x: 314, y: 175 } },
    { paths: arrow([[314, 209], [314, 258]], 7, { strokeLineDash: [6, 6] }) },
    { paths: P(gen.rectangle(262, 262, 104, 46, { ...base, strokeLineDash: [7, 5], seed: 9 })), label: { k: 'twofa', x: 314, y: 285, size: 21 } },
    { paths: arrow([[402, 175], [452, 175]], 11) },
    { paths: P(gen.polygon([[530, 121], [606, 175], [530, 229], [454, 175]], { ...base, fill: 'rgba(125,155,255,0.45)', fillStyle: 'cross-hatch', seed: 13 })), label: { k: 'check', x: 530, y: 175, size: 21 } },
    { paths: arrow([[608, 175], [648, 175]], 15), label: { k: 'yes', x: 627, y: 158, size: 19, color: '#7D9BFF' } },
    { paths: P(gen.rectangle(652, 143, 126, 64, { ...base, ...blue, seed: 17 })), label: { text: 'ledger', x: 715, y: 175 } },
    { paths: arrow([[715, 209], [715, 262]], 19) },
    { paths: P(gen.rectangle(652, 266, 126, 52, { ...base, fill: 'rgba(64,192,87,0.75)', fillStyle: 'hachure', seed: 21 })), label: { text: '200 OK', x: 715, y: 292 } },
    { paths: arrow([[530, 231], [530, 264]], 23), label: { k: 'no', x: 548, y: 248, size: 19, color: '#fca5a5' } },
    { paths: P(gen.rectangle(462, 268, 136, 50, { ...base, fill: 'rgba(250,82,82,0.6)', fillStyle: 'hachure', seed: 25 })), label: { k: 'err', x: 530, y: 293, size: 21 } },
    { paths: P(gen.rectangle(566, 18, 196, 54, { ...base, stroke: '#ffd43b', fill: 'rgba(255,212,59,0.16)', fillStyle: 'solid', seed: 27 })), label: { k: 'sticky', x: 664, y: 45, size: 25, color: '#ffd43b', rotate: -3 } },
    { paths: arrow([[574, 64], [470, 92], [352, 138]], 29, { stroke: '#ffd43b', strokeWidth: 1.4 }) },
  ]
}

const TOOLS = [
  'M5 5h14v14H5z', // rectangle
  'M12 3l9 9-9 9-9-9z', // diamond
  'M12 4a8 8 0 1 0 0 16a8 8 0 1 0 0-16', // ellipse
  'M4 12h14M13 7l5 5-5 5', // arrow
  'M4 20l16-16', // line
  'M4 20c4-1 4-7 8-8s5 3 8-4', // pencil
  'M6 19L12 5l6 14M8.5 14h7', // text
]

/* ---------------------------------- scene 3 --------------------------------- */
const T = {
  h: 'text-glow font-semibold',
  q: 'text-mist italic',
  k: 'text-white/40',
  p: 'text-fog',
  s: 'text-[#a5d6ff]',
  m: 'text-royal font-semibold',
  ok: 'text-[#4ade80] font-semibold',
  er: 'text-[#f87171] font-semibold',
}
const SPEC = {
  pt: [
    [['h', '# spec: transferencia-entre-carteiras']],
    [['q', '> carteiras custodiais · valores em centavos']],
    [],
    [['h', '## contrato']],
    [['m', 'POST'], ['p', ' /v1/transfers']],
    [['k', 'headers  '], ['p', 'Idempotency-Key, Authorization']],
    [['k', 'body     '], ['p', '{ from, to, amount, otp }']],
    [['ok', '200'], ['k', ' → '], ['p', '{ id, status: '], ['s', '"settled"'], ['p', ' }']],
    [['er', '422'], ['k', ' → '], ['p', '{ error: '], ['s', '"insufficient_funds"'], ['p', ' }']],
    [],
    [['h', '## critérios de aceite']],
    { check: 'nunca deixa o saldo negativo' },
    { check: 'mesma Idempotency-Key → mesma resposta' },
    { check: '2FA obrigatório acima de R$ 1.000' },
    { check: 'publica o evento TransferSettled' },
  ],
  en: [
    [['h', '# spec: wallet-to-wallet-transfer']],
    [['q', '> custodial wallets · amounts in cents']],
    [],
    [['h', '## contract']],
    [['m', 'POST'], ['p', ' /v1/transfers']],
    [['k', 'headers  '], ['p', 'Idempotency-Key, Authorization']],
    [['k', 'body     '], ['p', '{ from, to, amount, otp }']],
    [['ok', '200'], ['k', ' → '], ['p', '{ id, status: '], ['s', '"settled"'], ['p', ' }']],
    [['er', '422'], ['k', ' → '], ['p', '{ error: '], ['s', '"insufficient_funds"'], ['p', ' }']],
    [],
    [['h', '## acceptance criteria']],
    { check: 'balance can never go negative' },
    { check: 'same Idempotency-Key → same response' },
    { check: '2FA required above R$ 1,000' },
    { check: 'publishes the TransferSettled event' },
  ],
}

/* ---------------------------------- scene 4 --------------------------------- */
const TREE = [
  { name: 'wallet-api', depth: 0, dir: true },
  { name: 'specs', depth: 1, dir: true },
  { name: 'transfers', depth: 2, dir: true },
  { name: 'spec.md', depth: 3, id: 'spec' },
  { name: 'contract.yaml', depth: 3, id: 'contract' },
  { name: 'acceptance.md', depth: 3, id: 'accept' },
  { name: 'src', depth: 1, dir: true },
  { name: 'transfers', depth: 2, dir: true, isNew: true },
  { name: 'transfer.controller.ts', depth: 3, isNew: true },
  { name: 'transfer.service.ts', depth: 3, isNew: true },
  { name: 'transfer.e2e.spec.ts', depth: 3, isNew: true },
  { name: 'CLAUDE.md', depth: 1, id: 'claude' },
]
const AGENT = {
  pt: {
    prompt: 'implemente specs/transfers seguindo o contrato',
    status: 'agente ativo',
    plan: 'Plano · 4 tarefas',
    todos: ['controller + validação do body', 'service idempotente + ledger', 'evento TransferSettled', 'testes de aceite'],
    write: 'Write(src/transfers/*) · 3 arquivos',
    tests: '18 passed · 0 failed',
    done: 'Pronto pra review → PR #42',
  },
  en: {
    prompt: 'implement specs/transfers following the contract',
    status: 'agent active',
    plan: 'Plan · 4 tasks',
    todos: ['controller + body validation', 'idempotent service + ledger', 'TransferSettled event', 'acceptance tests'],
    write: 'Write(src/transfers/*) · 3 files',
    tests: '18 passed · 0 failed',
    done: 'Ready for review → PR #42',
  },
}

/* ---------------------------------- scene 5 --------------------------------- */
const APP = {
  pt: { title: 'Transferir', from: 'De', wallet: 'Carteira principal', balance: 'R$ 1.240,00', to: 'Para', amount: 'Valor', value: 'R$ 250,00', send: 'Enviar transferência', sending: 'Enviando…', sent: 'Enviado', toast: 'Transferência concluída', again: 'clique pra enviar de novo', network: 'Rede', waiting: 'aguardando requisição…', response: 'Resposta' },
  en: { title: 'Transfer', from: 'From', wallet: 'Main wallet', balance: 'R$ 1,240.00', to: 'To', amount: 'Amount', value: 'R$ 250.00', send: 'Send transfer', sending: 'Sending…', sent: 'Sent', toast: 'Transfer settled', again: 'click to send again', network: 'Network', waiting: 'waiting for a request…', response: 'Response' },
}

// Timeline positions (arbitrary units — the whole timeline is scrubbed across the pin)
const AT = { s2: 2.6, s3: 6.5, s4: 10.4, s5: 15.6 }
const AUTO = AT.s5 + 0.5
const STEP_AT = [0, AT.s2, AT.s3, AT.s4, AT.s5]

/* Types `texts` into line elements as `state.n` advances; a caret sits on the active line.
   Pure function of n, so it scrubs both ways. */
function typer(lines, texts) {
  const state = { n: 0 }
  const total = texts.reduce((n, s) => n + s.length, 0)
  const render = () => {
    let left = Math.round(state.n)
    let active = -1
    texts.forEach((s, i) => {
      const k = Math.max(0, Math.min(s.length, left))
      left -= s.length
      if (active === -1 && k < s.length) active = i
      const line = lines[i]
      if (line) line.querySelector('[data-t]').textContent = s.slice(0, k)
    })
    if (active === -1) active = texts.length - 1
    lines.forEach((line, i) => {
      const caret = line.querySelector('[data-caret]')
      if (caret) caret.style.display = i === active ? 'inline-block' : 'none'
    })
  }
  render()
  return { state, total, render }
}

function Caret({ className = 'bg-royal' }) {
  return <span data-caret className={`ml-px inline-block h-[1.1em] w-[2px] translate-y-[0.18em] animate-[caret_1s_steps(1)_infinite] ${className}`} style={{ display: 'none' }} />
}

function Check({ className = '' }) {
  return (
    <svg viewBox="0 0 16 16" className={className} aria-hidden>
      <path data-tick d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" pathLength="1" strokeDasharray="1" strokeDashoffset="1" />
    </svg>
  )
}

export default function Workflow() {
  const root = useRef(null)
  const { t, lang } = useLang()
  const [step, setStep] = useState(0)
  const stepRef = useRef(0)
  const [vertical, setVertical] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches)
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)')
    const on = () => setVertical(mq.matches)
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [])
  const diagram = useMemo(() => buildDiagram(vertical), [vertical])
  const labels = LABELS[lang]
  const agent = AGENT[lang]
  const app = APP[lang]

  /* ---- live request (scene 5) ---- */
  const [run, setRun] = useState({ status: 'idle', id: '', ms: 0, at: '' })
  const runRef = useRef(run)
  runRef.current = run
  const timer = useRef(0)
  const jsonRef = useRef(null)
  const send = useCallback(() => {
    if (runRef.current.status === 'loading') return
    const id = 'tx_' + Math.random().toString(16).slice(2, 10)
    setRun({ status: 'loading', id, ms: 60 + Math.floor(Math.random() * 70), at: new Date().toISOString().slice(0, 19) + 'Z' })
    clearTimeout(timer.current)
    timer.current = setTimeout(() => setRun((r) => ({ ...r, status: 'done' })), 750)
  }, [])
  const reset = useCallback(() => {
    clearTimeout(timer.current)
    setRun((r) => ({ ...r, status: 'idle' }))
  }, [])
  useEffect(() => () => clearTimeout(timer.current), [])
  useEffect(() => reset(), [lang, reset])

  // the response types itself out in green once the request settles
  useEffect(() => {
    const pre = jsonRef.current
    if (!pre) return
    if (run.status !== 'done') {
      pre.textContent = ''
      return
    }
    const json = JSON.stringify({ id: run.id, status: 'settled', amount: 25000, currency: 'BRL', to: '0x8f3…a91c', settled_at: run.at }, null, 2)
    const s = { n: 0 }
    const tw = gsap.to(s, {
      n: json.length,
      duration: prefersReducedMotion() ? 0 : 0.9,
      ease: 'none',
      onUpdate: () => (pre.textContent = json.slice(0, Math.round(s.n))),
    })
    const pop = gsap.fromTo('[data-status-chip]', { scale: 0.6, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.6, ease: 'back.out(2.2)' })
    const toast = gsap.fromTo('[data-toast]', { y: 14, opacity: 0 }, { y: 0, opacity: 1, duration: 0.5, ease: 'power3.out' })
    return () => {
      tw.kill()
      pop.kill()
      toast.kill()
    }
  }, [run])

  useGSAP(
    () => {
      const q = gsap.utils.selector(root)
      const reduce = prefersReducedMotion()

      // title reveal (before the pin)
      const split = SplitText.create('[data-wf-title]', { type: 'words,chars', mask: 'chars' })
      gsap.from(split.chars, { yPercent: 110, stagger: 0.03, scrollTrigger: { trigger: '[data-wf-title]', start: 'top 85%' } })

      const scenes = q('[data-scene]')
      const titles = q('[data-wtitle]')
      gsap.set(scenes.slice(1), { autoAlpha: 0 })
      gsap.set(titles.slice(1), { autoAlpha: 0, y: 14 })

      const tl = gsap.timeline({ defaults: { ease: 'power2.out', duration: 0.5 } })
      const swap = (i, at) => {
        tl.to(scenes[i - 1], { autoAlpha: 0, y: -18, scale: 0.985, filter: 'blur(6px)', duration: 0.45, ease: 'power2.in' }, at)
          .fromTo(scenes[i], { autoAlpha: 0, y: 18, scale: 1.015, filter: 'blur(6px)' }, { autoAlpha: 1, y: 0, scale: 1, filter: 'blur(0px)', duration: 0.5 }, at + 0.3)
          .to(titles[i - 1], { y: -14, autoAlpha: 0, duration: 0.4 }, at)
          .fromTo(titles[i], { y: 14, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 0.4 }, at + 0.2)
      }

      // 1 — scribble: the notes type themselves
      const notes = typer(q('[data-note]'), NOTES[lang])
      tl.to(notes.state, { n: notes.total, duration: 2.3, ease: 'none', onUpdate: notes.render }, 0.1)

      // 2 — Excalidraw: every rough stroke draws on, then its label lands
      swap(1, AT.s2)
      q('[data-shape]').forEach((g, i) => {
        const at = AT.s2 + 0.7 + i * 0.15
        const strokes = g.querySelectorAll('path[data-stroke]')
        const fills = g.querySelectorAll('path[data-fill]')
        const label = g.querySelector('text')
        if (strokes.length) tl.fromTo(strokes, { strokeDashoffset: 1 }, { strokeDashoffset: 0, duration: 0.45, ease: 'none', stagger: 0.04 }, at)
        if (fills.length) tl.fromTo(fills, { opacity: 0 }, { opacity: 1, duration: 0.3 }, at + 0.2)
        if (label) tl.fromTo(label, { opacity: 0, y: 6 }, { opacity: 1, y: 0, duration: 0.3 }, at + 0.3)
      })

      // 3 — spec: lines write in, then the acceptance criteria tick
      swap(2, AT.s3)
      tl.fromTo(q('[data-spec-line]'), { opacity: 0, x: -12 }, { opacity: 1, x: 0, duration: 0.3, stagger: 0.12 }, AT.s3 + 0.6)
      tl.fromTo(q('[data-spec] [data-tick]'), { strokeDashoffset: 1 }, { strokeDashoffset: 0, duration: 0.25, stagger: 0.2, ease: 'none' }, '>-0.1')
      tl.fromTo(q('[data-ready]'), { opacity: 0, scale: 0.7 }, { opacity: 1, scale: 1, duration: 0.35, ease: 'back.out(2)' }, '>')

      // 4 — the agent enters the repo
      swap(3, AT.s4)
      tl.fromTo(q('[data-agent-chip]'), { x: 40, autoAlpha: 0 }, { x: 0, autoAlpha: 1, duration: 0.5, ease: 'back.out(1.6)' }, AT.s4 + 0.5)
      const prompt = typer(q('[data-prompt]'), [agent.prompt])
      tl.to(prompt.state, { n: prompt.total, duration: 0.9, ease: 'none', onUpdate: prompt.render }, AT.s4 + 0.7)
      const hlOn = { backgroundColor: 'rgba(45,92,255,0.28)', color: '#ffffff', duration: 0.2 }
      const hlOff = { backgroundColor: 'rgba(45,92,255,0)', color: 'rgba(201,210,227,0.7)', duration: 0.2 }
      const row = (id) => q(`[data-row="${id}"]`)
      const line = (n) => q(`[data-tline="${n}"]`)
      const show = (n, at) => tl.fromTo(line(n), { autoAlpha: 0, x: -8 }, { autoAlpha: 1, x: 0, duration: 0.25 }, at)
      let at = AT.s4 + 1.7
      ;[
        ['r1', 'claude'],
        ['r2', 'spec'],
        ['r3', 'contract'],
      ].forEach(([n, id], i, all) => {
        show(n, at)
        tl.fromTo(row(id), hlOff, hlOn, at)
        if (i < all.length - 1) tl.to(row(id), hlOff, at + 0.3)
        at += 0.3
      })
      tl.to(row('contract'), hlOff, at)
      show('plan', at)
      at += 0.25
      q('[data-todo]').forEach((todo, i) => {
        tl.fromTo(todo, { autoAlpha: 0, x: -6 }, { autoAlpha: 1, x: 0, duration: 0.2 }, at)
        tl.fromTo(todo.querySelector('[data-done]'), { opacity: 0 }, { opacity: 1, duration: 0.15 }, at + 0.15)
        at += 0.2
      })
      show('write', at)
      tl.fromTo(q('[data-new]'), { height: 0, autoAlpha: 0 }, { height: 22, autoAlpha: 1, duration: 0.25, stagger: 0.08 }, at + 0.05)
      at += 0.5
      show('bash', at)
      show('tests', at + 0.25)
      show('done', at + 0.55)

      // 5 — delivery
      swap(4, AT.s5)
      tl.to({}, { duration: 1.6 }, AT.s5 + 0.5) // hold — room to play with the button before the pin releases

      // step rail progress
      // each connector fills while its step plays, reaching the next circle as that step begins
      q('[data-rail]').forEach((seg, i) => tl.fromTo(seg, { scaleY: 0 }, { scaleY: 1, duration: STEP_AT[i + 1] - STEP_AT[i], ease: 'none' }, STEP_AT[i]))
      tl.fromTo(q('[data-rail-x]'), { scaleX: 0 }, { scaleX: 1, duration: tl.duration(), ease: 'none' }, 0)

      // scene 5 autoplay: a cursor walks to the button and clicks it
      let autoDone = false
      let autoTl = null
      const autoplay = () => {
        const btn = q('[data-send]')[0]
        const stage = q('[data-stage-5]')[0]
        const cur = q('[data-cursor-fake]')[0]
        if (!btn || !stage || !cur || reduce) return send()
        const b = btn.getBoundingClientRect()
        const s = stage.getBoundingClientRect()
        autoTl = gsap
          .timeline()
          .fromTo(cur, { x: s.width * 0.92, y: s.height * 0.96, autoAlpha: 0 }, { x: b.left - s.left + b.width * 0.62, y: b.top - s.top + b.height * 0.5, autoAlpha: 1, duration: 0.9, ease: 'power2.inOut' })
          .to(cur, { scale: 0.82, duration: 0.09, ease: 'power1.in' })
          .to(btn, { scale: 0.95, duration: 0.09, ease: 'power1.in' }, '<')
          .add(send)
          .to([cur, btn], { scale: 1, duration: 0.2, ease: 'power2.out' })
          .to(cur, { autoAlpha: 0, duration: 0.4, delay: 0.7 })
      }

      const pin = q('[data-pin]')[0]
      gsap.timeline({
        scrollTrigger: {
          trigger: pin,
          start: 'top top',
          end: () => '+=' + window.innerHeight * 6,
          pin: true,
          scrub: 0.7,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          onUpdate: (self) => {
            const time = self.progress * tl.duration()
            let idx = 0
            STEP_AT.forEach((s, i) => time >= s - 0.05 && (idx = i))
            if (idx !== stepRef.current) {
              stepRef.current = idx
              setStep(idx)
            }
            if (time >= AUTO && !autoDone) {
              autoDone = true
              autoplay()
            } else if (time < AUTO - 0.6 && autoDone) {
              autoDone = false
              autoTl?.kill()
              gsap.set(q('[data-cursor-fake]'), { autoAlpha: 0 })
              reset()
            }
          },
        },
      }).add(tl)

      return () => {
        split.revert()
        autoTl?.kill()
      }
    },
    { scope: root, dependencies: [lang, vertical], revertOnUpdate: true },
  )

  const busy = run.status === 'loading'
  const done = run.status === 'done'

  return (
    <section id="workflow" ref={root} className="relative">
      <div data-pin className="relative flex min-h-[100svh] flex-col justify-center gap-5 overflow-hidden pb-8 pt-[84px] sm:gap-8">
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-[700px] w-[900px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-30 blur-[140px]" style={{ background: 'radial-gradient(circle, #2D5CFF, transparent 65%)' }} />

        <div className="relative mx-auto w-full max-w-[1440px] px-5 sm:px-10">
          <span className="label text-mist">(03) — {t(COPY.label)}</span>
          <h2 key={lang} data-wf-title className="mt-3 font-display text-[clamp(2.4rem,5vw,4.75rem)] font-semibold leading-[0.95] tracking-[-0.045em] text-white">
            {t(COPY.title)}
          </h2>
        </div>

        <div className="relative mx-auto grid w-full max-w-[1440px] items-center gap-6 px-5 sm:px-10 lg:grid-cols-[minmax(240px,340px)_1fr] lg:gap-14">
          {/* step rail — desktop */}
          <ol className="relative hidden flex-col gap-1 lg:flex">
            {STEPS.map((s, i) => (
              <li key={i} className="relative flex gap-4 rounded-2xl py-3 pr-3">
                {/* connector to the next step — drawn only in the gap between the two circles */}
                {i < STEPS.length - 1 && (
                  <span className="absolute -bottom-3 left-[15px] top-[47px] w-px bg-white/10">
                    <span data-rail className="absolute inset-0 origin-top bg-gradient-to-b from-[#7D9BFF] to-[#2D5CFF] shadow-[0_0_12px_#2D5CFF]" />
                  </span>
                )}
                <span
                  className={`relative z-10 grid h-[31px] w-[31px] shrink-0 place-items-center rounded-full border font-mono text-[11px] transition-all duration-500 ${
                    step === i ? 'border-royal bg-royal text-white shadow-[0_0_20px_rgba(45,92,255,0.8)]' : step > i ? 'border-royal/60 bg-ink text-glow opacity-60' : 'border-white/15 bg-ink text-mist opacity-40'
                  }`}
                >
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div className={`flex flex-col gap-1 pt-1 transition-opacity duration-500 ${step === i ? 'opacity-100' : 'opacity-40'}`}>
                  <span className="font-display text-lg font-semibold text-white">{t(s.title)}</span>
                  <span className={`text-sm leading-snug text-mist transition-[max-height,opacity] duration-500 ${step === i ? 'max-h-24 opacity-100' : 'max-h-0 overflow-hidden opacity-0'}`}>{t(s.desc)}</span>
                </div>
              </li>
            ))}
          </ol>

          {/* step rail — mobile */}
          <div className="flex flex-col gap-2 lg:hidden">
            <div className="relative h-[2px] w-full overflow-hidden rounded-full bg-white/10">
              <span data-rail-x className="absolute inset-0 origin-left bg-gradient-to-r from-[#7D9BFF] to-[#2D5CFF]" />
            </div>
            <div className="flex items-baseline gap-3">
              <span className="font-mono text-xs text-glow">{String(step + 1).padStart(2, '0')} / 05</span>
              <span className="font-display text-base font-semibold text-white">{t(STEPS[step].title)}</span>
            </div>
            <span className="text-xs leading-snug text-mist">{t(STEPS[step].desc)}</span>
          </div>

          {/* the window — with the hero's dashed ring peeking from behind its top-right corner */}
          <div className="relative">
          <BgDottedCircle className="-right-8 -top-16 h-[160px] w-[160px] sm:-right-14 sm:-top-20 sm:h-[220px] sm:w-[220px]" degrees={225} drawOnScroll delay={1} start="top 70%" />
          <div className="glass relative overflow-hidden rounded-[22px]" style={{ borderRadius: 22 }}>
            <div className="flex h-11 items-center gap-3 border-b border-white/[0.07] bg-white/[0.02] px-4">
              <span className="flex gap-1.5">
                <i className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]/80" />
                <i className="h-2.5 w-2.5 rounded-full bg-[#febc2e]/80" />
                <i className="h-2.5 w-2.5 rounded-full bg-[#28c840]/80" />
              </span>
              <div className="relative h-5 flex-1 overflow-hidden text-center font-mono text-[11px] text-mist sm:text-xs">
                {STEPS.map((s, i) => (
                  <span key={i} data-wtitle className="absolute inset-0 truncate leading-5">
                    {t(s.file)}
                  </span>
                ))}
              </div>
              <span className="font-mono text-[10px] text-white/30 sm:text-[11px]">{String(step + 1).padStart(2, '0')}/05</span>
            </div>

            <div className="relative h-[min(56svh,500px)] sm:h-[min(60svh,520px)]">
              {/* 1 — notes */}
              <div data-scene className="absolute inset-0 overflow-hidden bg-[#0a0c12] p-5 sm:p-8">
                <div className="mb-4 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.14em] text-white/25 sm:text-[11px]">
                  <span className="h-1.5 w-1.5 rounded-full bg-royal" /> draft · {lang === 'pt' ? 'sem formatação' : 'no formatting'}
                </div>
                <div className="font-mono text-[11px] leading-[2.1] sm:text-[15px]">
                  {NOTES[lang].map((_, i) => (
                    <div key={`${lang}-${i}`} data-note className={`flex min-h-[2.1em] gap-3 sm:gap-5 ${i === 0 ? 'text-mist' : 'text-fog'}`}>
                      <span className="w-4 shrink-0 select-none text-right text-white/15">{i + 1}</span>
                      <span>
                        <span data-t />
                        <Caret />
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 2 — excalidraw */}
              <div
                data-scene
                className="absolute inset-0 overflow-hidden bg-[#121316]"
                style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.07) 1px, transparent 1px)', backgroundSize: '22px 22px' }}
              >
                <div className="absolute left-1/2 top-3 z-10 flex -translate-x-1/2 items-center gap-0.5 rounded-xl border border-white/10 bg-[#232329] p-1 shadow-[0_6px_24px_rgba(0,0,0,0.5)]">
                  {TOOLS.map((d, i) => (
                    <span key={i} className={`grid h-7 w-7 place-items-center rounded-lg sm:h-8 sm:w-8 ${i === 0 ? 'bg-[#403e6a] text-[#a8a5ff]' : 'text-white/60'}`}>
                      <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 sm:h-4 sm:w-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d={d} />
                      </svg>
                    </span>
                  ))}
                </div>
                <svg viewBox={vertical ? '0 0 400 540' : '0 0 800 330'} className="absolute inset-x-3 bottom-4 top-16 h-[calc(100%-5rem)] w-[calc(100%-1.5rem)]" preserveAspectRatio="xMidYMid meet" aria-hidden>
                  {diagram.map((shape, i) => (
                    <g key={i} data-shape>
                      {shape.paths.map((p, j) =>
                        p.fill && p.fill !== 'none' ? (
                          <path key={j} data-fill d={p.d} fill={p.fill} stroke="none" />
                        ) : (
                          <path key={j} data-stroke d={p.d} fill="none" stroke={p.stroke} strokeWidth={p.strokeWidth} strokeLinecap="round" pathLength="1" strokeDasharray="1" strokeDashoffset="1" />
                        ),
                      )}
                      {shape.label && (
                        <text
                          x={shape.label.x}
                          y={shape.label.y}
                          textAnchor={shape.label.anchor || 'middle'}
                          dominantBaseline="central"
                          fontFamily="Caveat, cursive"
                          fontWeight="600"
                          fontSize={shape.label.size || 24}
                          fill={shape.label.color || '#eef1f8'}
                          transform={shape.label.rotate ? `rotate(${shape.label.rotate} ${shape.label.x} ${shape.label.y})` : undefined}
                          opacity="0"
                        >
                          {shape.label.text || labels[shape.label.k]}
                        </text>
                      )}
                    </g>
                  ))}
                </svg>
              </div>

              {/* 3 — spec */}
              <div data-scene data-spec className="absolute inset-0 overflow-hidden bg-[#0a0c12] p-4 sm:p-7">
                <span data-ready className="absolute right-4 top-4 rounded-full border border-[#4ade80]/30 bg-[#4ade80]/10 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.12em] text-[#4ade80] sm:right-6 sm:top-6 sm:text-[10px]">
                  ✦ prompt-ready
                </span>
                <div className="font-mono text-[10.5px] leading-[1.95] sm:text-[13.5px]">
                  {SPEC[lang].map((ln, i) => (
                    <div key={`${lang}-${i}`} data-spec-line className="flex min-h-[1.95em] gap-3 sm:gap-5">
                      <span className="w-4 shrink-0 select-none text-right text-white/15">{i + 1}</span>
                      {Array.isArray(ln) ? (
                        <span className="whitespace-pre">
                          {ln.map(([c, s], j) => (
                            <span key={j} className={T[c]}>
                              {s}
                            </span>
                          ))}
                        </span>
                      ) : (
                        <span className="flex items-center gap-2 text-fog">
                          <span className="grid h-[1.15em] w-[1.15em] place-items-center rounded-[4px] border border-[#4ade80]/50 bg-[#4ade80]/10 text-[#4ade80]">
                            <Check className="h-[0.8em] w-[0.8em]" />
                          </span>
                          {ln.check}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* 4 — agent */}
              <div data-scene className="absolute inset-0 grid overflow-hidden bg-[#07080c] sm:grid-cols-[minmax(190px,0.8fr)_1.4fr]">
                <div className="hidden flex-col border-r border-white/[0.06] bg-white/[0.015] py-4 sm:flex">
                  <span className="mb-2 px-4 font-mono text-[10px] uppercase tracking-[0.14em] text-white/30">Explorer</span>
                  {TREE.map((r, i) => (
                    <div
                      key={i}
                      {...(r.id ? { 'data-row': r.id } : {})}
                      {...(r.isNew ? { 'data-new': true } : {})}
                      className="flex h-[22px] shrink-0 items-center gap-1.5 overflow-hidden whitespace-nowrap pr-3 font-mono text-[11.5px] text-fog/70"
                      style={{ paddingLeft: 14 + r.depth * 14 }}
                    >
                      <span className={r.dir ? 'text-glow' : 'text-white/30'}>{r.dir ? '▾' : '·'}</span>
                      <span className={r.dir ? 'text-fog' : ''}>{r.name}</span>
                      {r.isNew && !r.dir && <span className="ml-auto text-[10px] font-semibold text-[#4ade80]">A</span>}
                    </div>
                  ))}
                </div>
                <div className="flex min-w-0 flex-col">
                  <div className="flex h-10 items-center justify-between border-b border-white/[0.06] px-4">
                    <span className="font-mono text-[10px] text-white/35 sm:text-[11px]">~/wallet-api</span>
                    <span data-agent-chip className="flex items-center gap-2 rounded-full border border-royal/40 bg-royal/15 px-2.5 py-1 font-mono text-[10px] text-white sm:text-[11px]">
                      <span className="text-[#e8794a]">✻</span> Claude Code
                      <span className="relative flex h-1.5 w-1.5">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#4ade80] opacity-75" />
                        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#4ade80]" />
                      </span>
                      <span className="hidden text-white/50 sm:inline">{agent.status}</span>
                    </span>
                  </div>
                  <div className="flex flex-col gap-[3px] overflow-hidden p-4 font-mono text-[10.5px] leading-[1.7] text-fog sm:text-[12.5px]">
                    <div data-prompt className="mb-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-white">
                      <span className="mr-2 text-glow">&gt;</span>
                      <span data-t />
                      <Caret className="bg-white/80" />
                    </div>
                    <div data-tline="r1"><Dot /> Read(<span className="text-white">CLAUDE.md</span>)</div>
                    <div data-tline="r2"><Dot /> Read(<span className="text-white">specs/transfers/spec.md</span>)</div>
                    <div data-tline="r3"><Dot /> Read(<span className="text-white">specs/transfers/contract.yaml</span>)</div>
                    <div data-tline="plan"><Dot /> {agent.plan}</div>
                    {agent.todos.map((todo, i) => (
                      <div key={`${lang}-${i}`} data-todo className="flex gap-2 pl-4">
                        <span className="text-white/25">{i === 0 ? '⎿' : ' '}</span>
                        <span className="relative">
                          <span className="text-white/30">☐</span>
                          <span data-done className="absolute inset-0 text-[#4ade80]">☒</span>
                        </span>
                        <span className="truncate">{todo}</span>
                      </div>
                    ))}
                    <div data-tline="write"><Dot /> {agent.write}</div>
                    <div data-tline="bash"><Dot /> Bash(<span className="text-white">npm test</span>)</div>
                    <div data-tline="tests" className="pl-4 text-[#4ade80]">
                      <span className="text-white/25">⎿</span> {agent.tests}
                    </div>
                    <div data-tline="done" className="mt-1 text-white"><Dot color="text-[#4ade80]" /> {agent.done}</div>
                  </div>
                </div>
              </div>

              {/* 5 — delivery */}
              <div data-scene data-stage-5 className="absolute inset-0 grid content-center gap-3 overflow-hidden p-3 sm:p-6 md:grid-cols-[0.9fr_1.1fr] md:gap-5" style={{ background: 'radial-gradient(120% 90% at 0% 0%, rgba(45,92,255,0.18), transparent 60%), #08090e' }}>
                {/* the app */}
                <div className="relative flex flex-col gap-2.5 rounded-2xl border border-white/10 bg-white/[0.03] p-4 sm:gap-3 sm:p-5">
                  <span className="font-display text-lg font-semibold text-white sm:text-xl">{app.title}</span>
                  <div className="hidden justify-between rounded-xl bg-white/[0.04] px-3 py-2 text-xs sm:flex">
                    <span className="text-mist">{app.from}</span>
                    <span className="text-fog">
                      {app.wallet} · <span className="text-white">{app.balance}</span>
                    </span>
                  </div>
                  <div className="flex justify-between rounded-xl bg-white/[0.04] px-3 py-2 text-xs">
                    <span className="text-mist">{app.to}</span>
                    <span className="font-mono text-fog">0x8f3…a91c</span>
                  </div>
                  <div className="flex items-end justify-between px-1">
                    <span className="text-xs text-mist">{app.amount}</span>
                    <span className="font-display text-2xl font-semibold tracking-tight text-white sm:text-3xl">{app.value}</span>
                  </div>
                  <button
                    data-send
                    type="button"
                    onClick={send}
                    disabled={busy}
                    className={`group relative mt-1 flex h-11 items-center justify-center gap-2 overflow-hidden rounded-full text-sm font-medium text-white transition-[box-shadow,filter] duration-300 sm:h-12 ${
                      done ? 'bg-[#16a34a] shadow-[0_0_30px_rgba(74,222,128,0.45)]' : 'bg-brand shadow-[0_0_30px_rgba(45,92,255,0.45)] hover:brightness-110'
                    }`}
                  >
                    {busy && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />}
                    {busy ? app.sending : done ? `${app.sent} ✓` : app.send}
                    <span className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/4 -skew-x-12 bg-white/25 blur-md transition-transform duration-700 group-hover:translate-x-[520%]" />
                  </button>
                  <span className="hidden text-center text-[10px] text-white/30 sm:block">{done ? app.again : ' '}</span>
                  {done && (
                    <div data-toast className="absolute -top-3 right-3 flex items-center gap-1.5 rounded-full border border-[#4ade80]/40 bg-[#0b1a12] px-3 py-1 text-[11px] text-[#4ade80] shadow-[0_8px_24px_rgba(0,0,0,0.5)]">
                      ✓ {app.toast}
                    </div>
                  )}
                </div>

                {/* network panel */}
                <div className="flex min-h-[180px] flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#050608] font-mono text-[10.5px] sm:min-h-[260px] sm:text-[12px]">
                  <div className="flex items-center justify-between border-b border-white/[0.06] px-4 py-2.5 text-white/40">
                    <span className="uppercase tracking-[0.14em]">{app.network}</span>
                    {run.status !== 'idle' && <span>{done ? `${run.ms} ms` : '…'}</span>}
                  </div>
                  {run.status === 'idle' ? (
                    <div className="flex flex-1 items-center justify-center gap-2 text-white/25">
                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white/30" /> {app.waiting}
                    </div>
                  ) : (
                    <div className="flex flex-1 flex-col gap-2 p-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded bg-royal/20 px-1.5 py-0.5 font-semibold text-glow">POST</span>
                        <span className="text-fog">/v1/transfers</span>
                        {busy ? (
                          <span className="ml-auto flex items-center gap-1.5 text-white/40">
                            <span className="h-3 w-3 animate-spin rounded-full border border-white/20 border-t-white/70" /> pending
                          </span>
                        ) : (
                          <span data-status-chip className="ml-auto flex items-center gap-1.5 rounded-lg border border-[#4ade80]/40 bg-[#4ade80]/15 px-2.5 py-1 text-[12px] font-bold text-[#4ade80] shadow-[0_0_24px_rgba(74,222,128,0.4)] sm:text-[14px]">
                            <span className="h-1.5 w-1.5 rounded-full bg-[#4ade80] shadow-[0_0_8px_#4ade80]" /> 200 OK
                          </span>
                        )}
                      </div>
                      <span className="hidden truncate text-white/30 sm:block">Idempotency-Key: {run.id.replace('tx_', 'idk_')}</span>
                      {done && <span className="mt-1 hidden text-[10px] uppercase tracking-[0.14em] text-white/30 sm:block">{app.response}</span>}
                      <pre ref={jsonRef} className="whitespace-pre-wrap break-all text-[#4ade80] [text-shadow:0_0_12px_rgba(74,222,128,0.35)]" />
                    </div>
                  )}
                </div>

                {/* the autoplay cursor */}
                <svg data-cursor-fake viewBox="0 0 24 24" className="pointer-events-none absolute left-0 top-0 z-20 h-6 w-6 drop-shadow-[0_4px_10px_rgba(0,0,0,0.6)]" style={{ visibility: 'hidden' }} aria-hidden>
                  <path d="M4 2l16 9.5-7 1.5-3.5 7z" fill="#fff" stroke="#05060a" strokeWidth="1.4" strokeLinejoin="round" />
                </svg>
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function Dot({ color = 'text-glow' }) {
  return <span className={`mr-1 ${color}`}>⏺</span>
}
